package diana.soleil.design.iraniancanadienbazzar.core.firebase

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.EmailAuthProvider
import dev.gitlive.firebase.auth.FirebaseAuthInvalidCredentialsException
import dev.gitlive.firebase.auth.FirebaseAuthInvalidUserException
import dev.gitlive.firebase.auth.FirebaseAuthUserCollisionException
import dev.gitlive.firebase.auth.FirebaseAuthWeakPasswordException
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.GoogleAuthProvider
import dev.gitlive.firebase.auth.OAuthProvider
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.functions.functions
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.merge

/**
 * Email/password authentication over the GitLive Firebase Auth SDK, returning [DataResult] instead of
 * throwing so callers render error states without try-catch (mirrors the repository contract). The
 * Firebase app is initialized at each platform entry point before this is used (see [FirebaseServices]).
 *
 * Social sign-in (Google/Apple) needs platform credential plumbing and lands later behind this same
 * interface. Per-user data repositories in `:core:data` read [currentUser]/[authState] for the uid, so
 * feature modules never depend on `:core:firebase` directly.
 */
interface AuthService {
    /** Emits the signed-in [AuthUser] (or `null` when signed out) and on every auth state change. */
    val authState: Flow<AuthUser?>

    /** Synchronous snapshot of the current user, or `null` if signed out. */
    val currentUser: AuthUser?

    suspend fun signIn(email: String, password: String): DataResult<AuthUser>

    /**
     * Creates an email/password account. When an **anonymous** session is active the credential is
     * **linked** onto it instead, so the guest keeps their uid (and any per-uid data) across the
     * upgrade to a real account.
     */
    suspend fun signUp(email: String, password: String): DataResult<AuthUser>

    /**
     * Signs in anonymously so guests have a uid for per-user data (favorites sync, reviews prompt to
     * upgrade). Reuses the existing anonymous session if one is already active.
     */
    suspend fun signInAnonymously(): DataResult<AuthUser>

    /**
     * Completes Google sign-in from a native Google **ID token** (obtained per-platform: Android
     * Credential Manager, iOS GoogleSignIn, web popup). Links onto an active anonymous session so a
     * guest keeps their uid + data across the upgrade (mirrors [signUp]); if the Google account is
     * already a Mahaleh account, signs into that instead of dead-ending on a collision.
     */
    suspend fun signInWithGoogle(idToken: String, accessToken: String?): DataResult<AuthUser>

    /**
     * Completes Sign in with Apple from a native Apple identity token + the **un-hashed** nonce of
     * the same request (Firebase verifies the nonce against the token's claim). Links onto an active
     * anonymous session and falls back to plain sign-in on collision, exactly like
     * [signInWithGoogle]. Required on iOS by App Review 4.8 because we also offer Google.
     */
    suspend fun signInWithApple(idToken: String, rawNonce: String): DataResult<AuthUser>

    suspend fun sendPasswordReset(email: String): DataResult<Unit>

    /**
     * Mirrors the member's chosen display name onto the **Auth** user. Email/password sign-up never
     * sets one (Google/Apple supply theirs), which left `currentUser.displayName` null and any code
     * snapshotting it writing an empty name — the blank name tags in Messages (owner report
     * 2026-09-17). Called whenever the profile name is saved; best-effort at the call site.
     */
    suspend fun updateDisplayName(name: String): DataResult<Unit>

    suspend fun signOut()

    /**
     * Permanently deletes the signed-in account: reauthenticates with [password] (email accounts —
     * wrong password surfaces as [AppError.InvalidCredentials]), then calls the `deleteAccount`
     * Function which wipes the user's Firestore data and the Auth user (see `firebase/functions`),
     * and finally clears the local session. Store-required (Apple 5.1.1(v) / Play User Data).
     */
    suspend fun deleteAccount(password: String?): DataResult<Unit>
}

internal class DefaultAuthService : AuthService {
    private val auth get() = Firebase.auth

    // Linking a credential onto an anonymous user keeps the same user object, so the SDK's auth-state
    // listener does NOT fire (on any platform). [linkEvents] re-emits the current user after a link so
    // observers (session ViewModel, users/{uid} sync) see the anonymous→real flip.
    private val linkEvents = MutableSharedFlow<Unit>(extraBufferCapacity = 1)

    override val authState: Flow<AuthUser?> = merge(
        auth.authStateChanged.map { it?.toAuthUser() },
        linkEvents.map { auth.currentUser?.toAuthUser() },
    )

    override val currentUser: AuthUser? get() = auth.currentUser?.toAuthUser()

    override suspend fun signIn(email: String, password: String): DataResult<AuthUser> = authCall {
        auth.signInWithEmailAndPassword(email.trim(), password).user.require()
    }

    override suspend fun signUp(email: String, password: String): DataResult<AuthUser> = authCall {
        val anonymous = auth.currentUser?.takeIf { it.isAnonymous }
        if (anonymous != null) {
            val credential = EmailAuthProvider.credential(email.trim(), password)
            val linked = anonymous.linkWithCredential(credential).user.require()
            linkEvents.tryEmit(Unit)
            linked
        } else {
            auth.createUserWithEmailAndPassword(email.trim(), password).user.require()
        }
    }

    override suspend fun signInAnonymously(): DataResult<AuthUser> = authCall {
        auth.signInAnonymously().user.require()
    }

    override suspend fun signInWithGoogle(idToken: String, accessToken: String?): DataResult<AuthUser> = authCall {
        val credential = GoogleAuthProvider.credential(idToken, accessToken)
        val anonymous = auth.currentUser?.takeIf { it.isAnonymous }
        if (anonymous != null) {
            try {
                val linked = anonymous.linkWithCredential(credential).user.require()
                linkEvents.tryEmit(Unit)
                linked
            } catch (e: FirebaseAuthUserCollisionException) {
                // The Google account is already a Mahaleh account — sign into it rather than fail.
                // (The guest's anon uid is abandoned; acceptable vs. a dead-end error.)
                auth.signInWithCredential(credential).user.require()
            }
        } else {
            auth.signInWithCredential(credential).user.require()
        }
    }

    override suspend fun signInWithApple(idToken: String, rawNonce: String): DataResult<AuthUser> = authCall {
        val credential = OAuthProvider.credential(
            providerId = "apple.com",
            idToken = idToken,
            rawNonce = rawNonce,
        )
        val anonymous = auth.currentUser?.takeIf { it.isAnonymous }
        if (anonymous != null) {
            try {
                val linked = anonymous.linkWithCredential(credential).user.require()
                linkEvents.tryEmit(Unit)
                linked
            } catch (e: FirebaseAuthUserCollisionException) {
                // Already a Mahaleh account — sign into it rather than dead-end (mirrors Google).
                auth.signInWithCredential(credential).user.require()
            }
        } else {
            auth.signInWithCredential(credential).user.require()
        }
    }

    override suspend fun sendPasswordReset(email: String): DataResult<Unit> = try {
        auth.sendPasswordResetEmail(email.trim())
        DataResult.Success(Unit)
    } catch (e: FirebaseAuthInvalidUserException) {
        // Account-enumeration guard: a reset request for an email with NO account reports the SAME
        // success as one with an account. Never surface "invalid credentials" here — the user typed
        // no password, and telling them "no such account" would leak which emails are registered.
        DataResult.Success(Unit)
    } catch (e: Exception) {
        DataResult.Failure(if (e.isNetworkError()) AppError.Network(e) else AppError.Unknown(e))
    }

    override suspend fun updateDisplayName(name: String): DataResult<Unit> = try {
        val user = auth.currentUser
        val trimmed = name.trim()
        if (user == null || user.isAnonymous || trimmed.isEmpty()) {
            DataResult.Failure(AppError.Unauthorized())
        } else {
            // photoUrl defaults to the user's current value, so this only touches the name.
            user.updateProfile(displayName = trimmed)
            DataResult.Success(Unit)
        }
    } catch (e: Exception) {
        DataResult.Failure(if (e.isNetworkError()) AppError.Network(e) else AppError.Unknown(e))
    }

    override suspend fun signOut() {
        auth.signOut()
    }

    override suspend fun deleteAccount(password: String?): DataResult<Unit> = authCall {
        val user = auth.currentUser ?: error("No signed-in user")
        if (!password.isNullOrBlank()) {
            val email = user.email ?: error("Account has no email to reauthenticate with")
            user.reauthenticate(EmailAuthProvider.credential(email, password))
        }
        // Server deletes Firestore data + the Auth user; local sign-out clears the dead session
        // (the SDK only notices the remote deletion on its next token refresh).
        Firebase.functions("us-central1").httpsCallable("deleteAccount").invoke()
        auth.signOut()
    }

    private fun FirebaseUser?.require(): AuthUser =
        (this ?: error("Firebase returned a null user")).toAuthUser()

    private fun FirebaseUser.toAuthUser() = AuthUser(
        uid = uid,
        email = email,
        displayName = displayName,
        isAnonymous = isAnonymous,
    )

    /** Runs an auth call and maps GitLive's exceptions onto the coarse [AppError] categories. */
    private suspend fun <T> authCall(block: suspend () -> T): DataResult<T> = try {
        DataResult.Success(block())
    } catch (e: FirebaseAuthUserCollisionException) {
        DataResult.Failure(AppError.Conflict(e))
    } catch (e: FirebaseAuthWeakPasswordException) {
        DataResult.Failure(AppError.InvalidCredentials(e))
    } catch (e: FirebaseAuthInvalidCredentialsException) {
        DataResult.Failure(AppError.InvalidCredentials(e))
    } catch (e: FirebaseAuthInvalidUserException) {
        DataResult.Failure(AppError.InvalidCredentials(e))
    } catch (e: Exception) {
        // GitLive exposes no common network-exception type, so connectivity failures otherwise fall
        // through to Unknown ("Something went wrong") — losing the actionable "No connection" copy.
        // Detect them by the platform message (Android FirebaseNetworkException / JS
        // auth/network-request-failed) so SignInStatus.Network becomes reachable for every auth flow.
        DataResult.Failure(if (e.isNetworkError()) AppError.Network(e) else AppError.Unknown(e))
    }

    /** Cross-platform network-failure heuristic (GitLive has no shared network exception class). */
    private fun Throwable.isNetworkError(): Boolean {
        val text = (message ?: "").lowercase()
        return text.contains("network") || text.contains("timeout") ||
            text.contains("unreachable") || text.contains("connection")
    }
}
